import * as React from 'react';
import { UserDetailMutationInput, StateNodeEdge, TownNodeEdge, StateNode,
  TownNode, EmployeeNodeEdge, UserDetailNodeEdge, AreaNodeEdge } from '../../../schema';
import { Button, notify, Input, Card, Page } from 'sha-el-design';
import { userDetailSchema } from './constant';
import { MultipleStateDropdown } from '../Common/MultipleStateDropdown';
import { MultipleTownDropdown } from '../Common/MultipleTownDropdown';
import { MultipleAreaDropdown } from '../Common/MultipleAreaDropdown';
import { EmployeeDropdown } from '../Common/EmployeeDropdown';
import { fromGlobalId, Inject } from '../../helpers';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import { UserService } from '../../service/userService';
import { UserDetailDropdown } from '../Common/UserDetailDropdown';
import { MdHome, MdArrowBack } from 'react-icons/md';
import { Link } from 'react-router-dom';
import '../Common/listPageStyles';

export class AddUserDetail extends React.Component<Props, State> {
  @Inject userService: UserService;

  // The role decides what the whole account can do, but it used to be an
  // AutoComplete labelled 'Uesr Type' buried second to last in the grid.
  static ROLES = [
    { type: 'ADMIN', blurb: 'Full access: masters, reports, assignment and calling.' },
    { type: 'SUPERVISOR', blurb: 'Assigns work to callers and monitors progress.' },
    { type: 'CALLER', blurb: 'Works through the call queues assigned to them.' },
  ];

  constructor(props: Props) {
    super(props);

    this.state = {
      userDetail: this.props.userDetail
        ? JSON.parse(JSON.stringify(this.props.userDetail))
        : JSON.parse(JSON.stringify(userDetailSchema.initialize())),
      error: null,
      stateList: this.props.stateList || [],
      townList: this.props.townList || [],
      selectedEmployee: null,
      selectedSupervisor: null,
      selectedStates: [],
      selectedTowns: [],
      selectedAreas: [],
      lockSubmit: false,
    };
  }

  // EditUserDetail renders this same component with the record as props.
  isEdit = () => !!(this.props.userDetail && this.props.userDetail.id);

  onChange = (name: string, value: any) => {
    const { userDetail } = this.state;
    userDetail[name] = value;
    this.setState({ userDetail });
  }

  stateListChangeHandler = (states: StateNodeEdge[]) => {
    this.setState({ stateList: states.map((state) => state.node) });
  }

  townListChangeHandler = (towns: TownNodeEdge[]) => {
    this.setState({ townList: towns.map((town) => town.node) });
  }

  submit = () => {

    this.setState({ lockSubmit: true });

    const userDetail = { ...this.state.userDetail };

    userDetail.states = nestedAccess(userDetail, 'states', 'length') ?
      ((userDetail.states[0] as any).hasOwnProperty('state')
        ? [] : userDetail.states.map((v) => String(fromGlobalId(v)))) : [] as any[];
    userDetail.towns = nestedAccess(userDetail, 'towns', 'length') ?
      ((userDetail.towns[0] as any).hasOwnProperty('town')
        ? [] : userDetail.towns.map((v) => String(fromGlobalId(v)))) : [] as any[];
    userDetail.areas = nestedAccess(userDetail, 'areas', 'length') ?
      ((userDetail.areas[0] as any).hasOwnProperty('area')
        ? [] : userDetail.areas.map((v) => String(fromGlobalId(v)))) : [] as any[];

    this.userService.addUserDetail(
      userDetail,
      (response) => {
        if (!response.data.createUserDetail && response.errors) {
          return notify({
            type: 'error',
            title: response.errors[0].message,
            callBack: () => {
              this.setState({ lockSubmit: false });
            },
            duration: 3500,
          });
        }
        const errors = response.data.createUserDetail.errors;
        if (!errors) {
          notify({
            type: 'success',
            title: this.isEdit() ? 'User Detail Updated' : 'User Detail Added',
            callBack: () => {
              // On edit, keep the saved values on screen; blanking the form
              // after saving an existing user read as data loss.
              if (this.isEdit()) {
                this.setState({ lockSubmit: false });
              } else {
                this.clear();
              }
            },
          });
        } else {
          notify({
            type: 'error',
            title: errors[0].field.toUpperCase(),
            message: errors[0].messages[0],
            callBack: () => {
              this.setState({ lockSubmit: false });
            },
            duration: 3500,
          });
        }
      },
      (e) => e,
    );
  }

  clear = () => {
    // Previously reset only `userDetail`, so every dropdown kept displaying
    // the old selection while the payload underneath was already blank.
    // Reset both sides together; on edit, restore the incoming record.
    this.setState({
      userDetail: this.isEdit()
        ? JSON.parse(JSON.stringify(this.props.userDetail))
        : JSON.parse(JSON.stringify(userDetailSchema.initialize())),
      stateList: this.props.stateList || [],
      townList: this.props.townList || [],
      selectedEmployee: null,
      selectedSupervisor: null,
      selectedStates: [],
      selectedTowns: [],
      selectedAreas: [],
      lockSubmit: false,
    });
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'User Detail', link: '/dashboard/user_detail' },
      this.isEdit()
        ? { text: 'Edit User Detail', link: `/dashboard/user_detail/${this.props.userDetail.id}` }
        : { text: 'Add User Detail', link: '/dashboard/user_detail/add' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  rolePicker = () => {
    const { userDetail } = this.state;
    return (
      <div className='cs-form__roles' role='radiogroup' aria-label='User type'>
        {AddUserDetail.ROLES.map((r) => {
          const selected = userDetail.userType === r.type;
          return (
            <button
              key={r.type}
              type='button'
              role='radio'
              aria-checked={selected}
              className={
                'cs-form__role cs-form__role--' + r.type.toLowerCase() +
                (selected ? ' cs-form__role--selected' : '')
              }
              onClick={() => this.onChange('userType', r.type)}
            >
              <span className='cs-form__role-name'>
                {r.type}
                {selected && <span aria-hidden='true'>✓</span>}
              </span>
              <span className='cs-form__role-blurb'>{r.blurb}</span>
            </button>
          );
        })}
      </div>
    );
  }

  render() {
    const {
      userDetail,
      stateList,
      townList,
      selectedEmployee,
      selectedSupervisor,
      selectedStates,
      selectedTowns,
      selectedAreas,
      lockSubmit,
    } = this.state;
    const error = { ...this.state.error };
    const isEdit = this.isEdit();
    return (
      <div className='cs-form'>
        <Page
          title={isEdit ? 'Edit User Detail' : 'Add User Detail'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard/user_detail'}>
              <MdArrowBack />
            </Link>
          }
        >
          <div className='cs-form__card'>
            <Card>
              <section className='cs-form__section'>
                <h4 className='cs-form__legend'>Account</h4>
                <p className='cs-form__hint'>
                  The employee this login belongs to, and their sign-in credentials.
                </p>
                <div className='cs-form__grid'>
                  <div className='cs-form__wide'>
                    <EmployeeDropdown
                      value={selectedEmployee || this.props.selectedEmployee}
                      inputProps={{ error: error.employee }}
                      uniqueIdentifier={(v) => v.node.id}
                      displayProp={(v) => v && v.node.name}
                      onChange={(v) => {
                        this.onChange('employee', v.node.id);
                        this.setState({ selectedEmployee: v });
                      }}
                      label='Employee'
                      mode='single'
                    />
                  </div>
                  {/* Each Input sits in its own div: sha-el's Input renders
                      more than one root element, and the extra ones become
                      stray grid items that push Password into the next row. */}
                  <div>
                    <Input
                      value={userDetail.username}
                      type='text'
                      label='User Name'
                      required
                      onChange={(e) => this.onChange('username', e.target.value)}
                      error={nestedAccess(error, 'username')}
                    />
                  </div>
                  <div>
                    <Input
                      value={userDetail.password}
                      type='password'
                      label='Password'
                      required
                      onChange={(e) => this.onChange('password', e.target.value)}
                      error={nestedAccess(error, 'password')}
                    />
                  </div>
                </div>
              </section>

              <section className='cs-form__section'>
                <h4 className='cs-form__legend'>Role</h4>
                <p className='cs-form__hint'>
                  What this user can see and do. Callers also need the supervisor they report to.
                </p>
                {this.rolePicker()}
                {userDetail.userType === 'CALLER' && (
                  <div className='cs-form__supervisor'>
                    <UserDetailDropdown
                      value={selectedSupervisor || this.props.selectedSupervisor}
                      inputProps={{ error: error.supervisor }}
                      uniqueIdentifier={(v) => nestedAccess(v, 'node', 'id')}
                      displayProp={(v) => nestedAccess(v, 'node', 'employee', 'name')}
                      onChange={(v) => {
                        this.onChange('supervisor', v.node.id);
                        this.setState({ selectedSupervisor: v });
                      }}
                      label='Supervisor'
                      mode='single'
                      userType='ADMIN,SUPERVISOR'
                    />
                  </div>
                )}
              </section>

              <section className='cs-form__section'>
                <h4 className='cs-form__legend'>Territory</h4>
                <p className='cs-form__hint'>
                  The states, towns and areas this user covers. Towns follow the chosen
                  states; areas follow the chosen towns.
                </p>
                <div className='cs-form__grid cs-form__grid--3'>
                  <MultipleStateDropdown
                    value={(selectedStates.length ? selectedStates : this.props.selectedStates) || []}
                    inputProps={{ error: error.states }}
                    uniqueIdentifier={(v) => v.node.id}
                    displayProp={(v) => v && v.node.name}
                    onChange={(values) => {
                      if (Object.values(values.map((v) => v.node.id)).
                            filter((item) => item === values.map((v) => v.node.id).pop()).length === 1) {
                              this.onChange('states', values.map((v) => v.node.id));
                              this.stateListChangeHandler(values);
                              this.setState({ selectedStates: values });
                          }
                    }}
                    mode='multiple'
                    label='State'
                  />
                  <MultipleTownDropdown
                    stateList={stateList.map((state) => state.name).join(',')}
                    value={(selectedTowns.length ? selectedTowns : this.props.selectedTowns) || []}
                    inputProps={{ error: error.towns }}
                    uniqueIdentifier={(v) => v.node.id}
                    displayProp={(v) => v && v.node.name}
                    onChange={(values) => {
                      if (Object.values(values.map((v) => v.node.id)).
                            filter((item) => item === values.map((v) => v.node.id).pop()).length === 1) {
                              this.onChange('towns', values.map((v) => v.node.id));
                              this.townListChangeHandler(values);
                              this.setState({ selectedTowns: values });
                            }
                    }}
                    mode='multiple'
                    label='Town'
                  />
                  <MultipleAreaDropdown
                    townList={townList.map((town) => town.name).join(',')}
                    value={(selectedAreas.length ? selectedAreas : this.props.selectedAreas) || []}
                    inputProps={{ error: error.areas }}
                    uniqueIdentifier={(v) => v.node.id}
                    displayProp={(v) => v && v.node.name}
                    onChange={(values) => {
                      if (Object.values(values.map((v) => v.node.id)).
                            filter((item) => item === values.map((v) => v.node.id).pop()).length === 1) {
                              this.onChange('areas', values.map((v) => v.node.id));
                              this.setState({ selectedAreas: values });
                            }
                    }}
                    mode='multiple'
                    label='Area'
                  />
                </div>
              </section>

              <div className='cs-form__actions'>
                <Button
                  type='secondary'
                  onClick={this.clear}
                >
                  {isEdit ? 'Reset' : 'Clear'}
                </Button>
                <Button
                  type='primary'
                  onClick={this.submit}
                  disabled={lockSubmit}
                >
                  {lockSubmit ? 'Saving...' : isEdit ? 'Save Changes' : 'Create User'}
                </Button>
              </div>
            </Card>
          </div>
        </Page>
      </div>
    );
  }
}

interface State {
  userDetail: UserDetailMutationInput;
  error: Partial<{
    [key in userDetailKeys]: string;
  }>;
  stateList: StateNode[];
  townList: TownNode[];
  selectedEmployee: EmployeeNodeEdge;
  selectedSupervisor: UserDetailNodeEdge;
  selectedStates: StateNodeEdge[];
  selectedTowns: TownNodeEdge[];
  selectedAreas: AreaNodeEdge[];
  lockSubmit: boolean;
}

type userDetailKeys = keyof UserDetailMutationInput;

interface Props {
  userDetail: UserDetailMutationInput;
  selectedEmployee: EmployeeNodeEdge;
  selectedSupervisor: UserDetailNodeEdge;
  stateList: StateNode[];
  townList: TownNode[];
  selectedStates: StateNodeEdge[];
  selectedTowns: TownNodeEdge[];
  selectedAreas: AreaNodeEdge[];
}
